// Undo notice (mockup ui.snackbar, element 071): black pill at the bottom centre with the time left as a shrinking ring,
// [되돌리기] and ✕. One at a time; it closes when the screen changes. Read once by screen readers (#app-live).
//   snackbar({ text, sub, undo: () => restore(), undone: '되돌렸어요', ms: 8000 })
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import { create } from 'zustand';
import { useT } from '../i18n';
import { reduceMotion } from '../lib/media';
import { Icon } from './Icon';
import { announce, toast } from './layers';

export interface SnackbarOptions {
  text: string;
  sub?: string;
  /** Undo action (no button without it). */
  undo?: () => void;
  /** One-line toast after undoing. */
  undone?: string;
  /** How long it stays (default 8 s). */
  ms?: number;
}
interface SnackState {
  cur: (SnackbarOptions & { id: number; on: boolean }) | null;
}
const useSnack = create<SnackState>(() => ({ cur: null }));
let seq = 0;

export function snackbar(o: SnackbarOptions): { close(): void } {
  const id = ++seq;
  useSnack.setState({ cur: { ...o, id, on: false } });
  announce(o.text + (o.sub ? '. ' + o.sub : ''));
  return { close: () => closeSnackbar(id) };
}
export function closeSnackbar(id?: number): void {
  const cur = useSnack.getState().cur;
  if (!cur || (id != null && cur.id !== id)) return;
  useSnack.setState({ cur: { ...cur, on: false } });
  setTimeout(() => {
    if (useSnack.getState().cur?.id === cur.id) useSnack.setState({ cur: null });
  }, reduceMotion() ? 0 : 320);
}

/** Mount once inside the router (the app shell does it). */
export function SnackbarHost() {
  const t = useT();
  const cur = useSnack((s) => s.cur);
  const location = useLocation();
  const path = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== path.current) {
      path.current = location.pathname;
      closeSnackbar();
    }
  }, [location.pathname]);
  useEffect(() => {
    if (!cur) return;
    if (!cur.on) {
      const raf = requestAnimationFrame(() => useSnack.setState((s) => (s.cur && s.cur.id === cur.id ? { cur: { ...s.cur, on: true } } : s)));
      return () => cancelAnimationFrame(raf);
    }
    const tm = setTimeout(() => closeSnackbar(cur.id), cur.ms ?? 8000);
    return () => clearTimeout(tm);
  }, [cur]);
  if (!cur || typeof document === 'undefined') return null;
  return createPortal(
    <div className={'snack' + (cur.on ? ' is-on' : '')} style={{ ['--ms' as string]: `${cur.ms ?? 8000}ms` }}>
      <span className="snack__ic" aria-hidden="true">
        <Icon name="check" />
      </span>
      <span className="snack__text">
        <b>{cur.text}</b>
        {cur.sub ? <small>{cur.sub}</small> : null}
      </span>
      {cur.undo ? (
        <button
          className="snack__undo"
          type="button"
          onClick={() => {
            const fn = cur.undo!;
            closeSnackbar(cur.id);
            fn();
            if (cur.undone) toast(cur.undone, 'ok');
          }}
        >
          <svg className="snack__ring" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
          </svg>
          <span>{t('common.undo')}</span>
        </button>
      ) : null}
      <button className="snack__x" type="button" aria-label={t('common.close')} onClick={() => closeSnackbar(cur.id)}>
        <Icon name="x" />
      </button>
    </div>,
    document.body,
  );
}
